import React, { useRef, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { useLabels, useFlags } from 'wsm-common-data';
import propTypes from 'prop-types';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';
// import { fetchSubaruService } from '../server/fetchSubaruService'
import { useDispatch, useSelector } from 'react-redux';
import { addStories } from '../actions/subaruServices';
import './Stories.scss';
import {
	MOBILE,
	DESKTOP,
	DEFUALT_SUBARU_LOGO,
	YOUTUBE_EMBED_BASE_URL
} from '../utils/widgetInfoDefaults';

const buildMediaHtml = (media, title, logo) => {
	const mediaType = ({ type, path }, index) => {
		const mapKey = `${title}-${type}-${index}`;
		switch (type) {
			case 'youtube':
				return (
					<iframe
						key={mapKey}
						src={`${YOUTUBE_EMBED_BASE_URL}${path}`}
						className="mb-4 mb-sm-0 stories-youtube-video"
						frameBorder="0"
						allowFullScreen
						width="100%"
						height="300px"
						title={title}
					/>
				);
			case 'video':
				return (
					// eslint-disable-next-line jsx-a11y/media-has-caption
					<video
						key={mapKey}
						className="mb-4 mb-sm-0"
						preload="none"
						poster={
							logo !== undefined && logo !== null && logo !== ''
								? logo
								: DEFUALT_SUBARU_LOGO
						}
						controls
					>
						<source src={path} type="video/mp4" />
					</video>
				);
			case 'image':
				return (
					<img
						key={mapKey}
						className="mb-4 mb-sm-0"
						src={path}
						alt={title}
					/>
				);
			default:
				return <React.Fragment />;
		}
	};
	return media.map((mediaItem, i) => mediaType(mediaItem, i));
};

const Stories = ({
	stories,
	accountInfo,
	deviceType,
	lockFooter,
	unlockFooter
}) => {
	const labels = useLabels();
	const flags = useFlags();
	const dispatch = useDispatch();
	const StoriesContainer = useRef(null);
	const dealerCode = useSelector((state) => state.accountInfo.dealerCode);
	const [isLoading, setIsLoading] = useState(false);
	const [endofdata, setEndofdata] = useState(false);
	const [offset, setOffset] = useState(10);

	const handleScroll = () => {
		if (StoriesContainer.current !== null) {
			if (
				StoriesContainer.current.getBoundingClientRect().bottom <=
				window.innerHeight
			) {
				lockFooter();
			} else {
				unlockFooter();
			}
		}
	};

	const handleScrollDebounce =
		isBrowser && window._ ? window._.debounce(handleScroll, 100) : null;

	useEffect(() => {
		if (deviceType === DESKTOP) {
			window.addEventListener('scroll', handleScrollDebounce);
		}
		return () => {
			if (deviceType === DESKTOP) {
				window.removeEventListener('scroll', handleScrollDebounce);
			}
		};
	});

	if (!accountInfo) {
		return null;
	}

	const storiesHeading = labels
		.get('LOVE_PROMISE_STORIES_HEADING')
		.replace('$account.Name', accountInfo.name);
	const storiesSubheading = labels
		.get('LOVE_PROMISE_STORIES_SUBHEADING')
		.replace('$account.Name', accountInfo.name);

	// Lock share footer into position if scrolled to bottom of container

	const handleFBShare = (storykey) => {
		const shareURL = `${accountInfo.url}/subaru-love-promise.htm#${storykey}`;
		window.open(
			`https://www.facebook.com/sharer/sharer.php?u=${shareURL}`,
			'pop',
			'width=600, height=400, scrollbars=no'
		);
	};

	const handleSeeMoreStories = async (increase) => {
		setIsLoading(true);
		try {
			const retries = flags['ws-subaru-lp-fetchSubaruData-retries'];
			const timeout = flags['ws-subaru-lp-fetchSubaruData-timeout'];
			const response = await fetch(
				// eslint-disable-next-line max-len
				`/api/widget/ws-subaru-lp/dealercode/${dealerCode}/limit/${10}/offset/${offset}/retries/${retries}/timeout/${timeout}`
			);

			const data = await response.json();

			if (data.length < 10) {
				setEndofdata(true);
			}
			dispatch(addStories(data));
			setIsLoading(false);
			setOffset(offset + increase);
		} catch (e) {
			const msg = `Could not retrieve data from subaru services - ${e.message}`;
			logger.error(msg);
		}
	};

	const sliderSettings = {
		dots: deviceType === MOBILE,
		dotsClass: 'lp-stories-slider-dots'
	};

	const formatCompanyURL = (companyURL) => {
		if (
			companyURL.match(
				/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g
			)
		) {
			return companyURL;
		} else {
			return `//${companyURL}`;
		}
	};

	return (
		<div
			ref={StoriesContainer}
			className="love-promise-stories bg-white border"
		>
			<h2 className="ddc-heading-2 widget-heading ddc-font-size-xxlarge text-center mt-6 mt-sm-8 mb-0">
				{storiesHeading}
			</h2>
			<p className="text-center m-4 my-sm-8 mx-sm-15">
				{storiesSubheading}
			</p>
			{stories.map(
				(
					{
						title,
						body,
						firstName,
						lastName,
						partnerCompanyURL,
						partnerCompanyLogo,
						partnerCompanyName,
						dealerstorykey,
						media,
						videoThumbnailUri1
					},
					i
				) => {
					const mapKey = `${i}-${title}`;
					return (
						<div key={mapKey}>
							<div
								className={`story row px-6 d-flex flex-column-reverse flex-sm-row ${
									i % 2 === 0
										? ''
										: 'd-sm-flex flex-sm-row-reverse'
								}`}
							>
								<div className="col-sm-7">
									<h3 className="mt-0">
										{title} - {firstName}{' '}
										{lastName.split('')[0]}
									</h3>
									<p>{body}</p>
									<div className="row px-4 d-sm-flex justify-content-end">
										{partnerCompanyLogo && (
											<div className="col-sm-4">
												{partnerCompanyURL ? (
													<a
														href={formatCompanyURL(
															partnerCompanyURL
														)}
														target="_blank"
														rel="noreferrer"
													>
														<img
															src={
																partnerCompanyLogo
															}
															alt={
																partnerCompanyName
															}
														/>
													</a>
												) : (
													<img
														src={partnerCompanyLogo}
														alt={partnerCompanyName}
													/>
												)}
											</div>
										)}
										<div className="share-buttons col-sm-3 col-sm-offset-5 d-flex align-items-center justify-content-center text-center">
											<button
												type="button"
												className="btn p-0 lp-share-story-facebook-btn"
												onClick={() =>
													handleFBShare(
														dealerstorykey
													)
												}
											>
												<i className="ddc-icon ddc-icon-facebook mr-5 ddc-font-size-jumbo" />
											</button>
											<a
												href={`https://twitter.com/share?url=${accountInfo.url}/subaru-love-promise.htm&hashtags=SubaruLovePromise&text=${title}`}
												target="_blank"
												rel="noopener noreferrer"
											>
												<i className="ddc-icon ddc-icon-twitter mr-sm-5 mx-3 ddc-font-size-jumbo" />
											</a>
										</div>
									</div>
								</div>
								<div className="col-sm-5 p-sm-8">
									{media.length > 1 ? (
										<Slider {...sliderSettings}>
											{buildMediaHtml(
												media,
												title,
												videoThumbnailUri1
											)}
										</Slider>
									) : (
										buildMediaHtml(
											media,
											title,
											videoThumbnailUri1
										)
									)}
								</div>
							</div>
							<div className="row w-100">
								<hr className="border-bottom border-black w-75 mb-5" />
							</div>
						</div>
					);
				}
			)}
			<div className="text-center mb-5">
				{isLoading ? (
					<img
						className="mx-auto"
						alt="loader spinning gif"
						src="https://static.dealer.com/v9/media/images/ajax-loaders/v1/white-ajax-loader-2.gif"
					/>
				) : (
					<button
						type="button"
						className="btn btn-primary btn-large"
						disabled={endofdata}
						onClick={() => handleSeeMoreStories(10)}
					>
						See More Stories
					</button>
				)}
			</div>
		</div>
	);
};

Stories.propTypes = {
	accountInfo: propTypes.shape({
		url: propTypes.string,
		name: propTypes.string
	}).isRequired,
	stories: propTypes.arrayOf(propTypes.object),
	deviceType: propTypes.string.isRequired,
	lockFooter: propTypes.func.isRequired,
	unlockFooter: propTypes.func.isRequired
};

export default Stories;
